import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { getSelectedOrderIsPreSubmission } from 'entities/Order/model/selectors/ordersSelectors';
import { getProposalQuantities } from 'entities/Proposal/lib/getProposalQuantities';
import { SelectedProposal } from 'entities/Proposal/model/types/ProposalSchema';

export const useOrderItemHooks = (item: CatalogItem) => {
    const { t } = useTranslation();
    let allSuppliersSubstituted = false;
    let someSuppliersSubstituted = false;
    let allSuppliersUnavailable = false;
    let substitutedProposals: SelectedProposal[] = [];
    let fulfilledProposals: SelectedProposal[] = [];
    let totalQtyFulfilled = 0;
    let totalQtySubstituted = 0;
    let totalQtyUnavailable = 0;
    let totalPriceFulfilled = 0;
    let unavailableSuppliers = 0;
    let fulfilledSupplierCount = 0;
    let substitutionTitle = '';

    const isPreSubmission = useSelector(getSelectedOrderIsPreSubmission);

    // Once an order is fulfilled, we can check if any suppliers have substituted the item for another
    if (!isPreSubmission) {
        item.selectedProposals?.forEach((proposal) => {
            if (proposal.selectedQuantity > 0) {
                if (proposal.replacedWith) {
                    totalQtySubstituted += proposal.selectedQuantity;
                    substitutedProposals.push(proposal);
                } else {
                    const { approvedQty, unavailableQty } =
                        getProposalQuantities(proposal);

                    totalQtyUnavailable += unavailableQty;
                    totalQtyFulfilled += approvedQty;
                    totalPriceFulfilled += proposal.price * approvedQty;
                    fulfilledProposals.push(proposal);

                    if (unavailableQty > 0) {
                        unavailableSuppliers += 1;
                    }

                    if (approvedQty > 0) {
                        fulfilledSupplierCount += 1;
                    }
                }
            }
        });

        const totalProposalsWithQty =
            substitutedProposals.length + fulfilledProposals.length;

        // Check if item is fully unavailable (nothing allocated, all unavailable)
        allSuppliersUnavailable =
            totalQtyFulfilled === 0 &&
            totalQtyUnavailable > 0 &&
            totalProposalsWithQty > 0;

        if (
            totalProposalsWithQty === substitutedProposals.length &&
            totalProposalsWithQty > 0
        ) {
            allSuppliersSubstituted = true;
            substitutionTitle = t('Unavailable');
        } else if (allSuppliersUnavailable) {
            substitutionTitle = t('Unavailable');
        } else if (substitutedProposals.length > 0) {
            someSuppliersSubstituted = true;
            substitutionTitle = t('Partially available');
        }
    }

    return {
        substitutionTitle,
        allSuppliersSubstituted,
        allSuppliersUnavailable,
        someSuppliersSubstituted,
        substitutedProposals,
        fulfilledProposals,
        totalQtyFulfilled,
        totalQtySubstituted,
        totalQtyUnavailable,
        totalPriceFulfilled,
        unavailableSuppliers,
        fulfilledSupplierCount,
    };
};
